import {
  PenTool, GitBranch, Columns3, CalendarDays, TrendingUp,
  BarChart3, FileText, Users, ClipboardList, ShieldCheck,
  Bot, BookOpen, CalendarRange, FlaskConical, TestTube2,
  Zap, GraduationCap, Sparkles, PackageOpen, BriefcaseBusiness,
  BookMarked, Scale, Brain, ScrollText, Microscope, MessageSquareText,
  ChevronRight, CheckCircle2, AlertCircle, Lock, Cpu, Quote,
  Inbox, NotebookPen, ShieldAlert, Map, ArrowRightLeft,
} from "lucide-react";

interface Feature {
  icon: React.ComponentType<any>;
  name: string;
  desc: string;
  ai?: boolean;
}

interface FeatureGroup {
  label: string;
  color: string;
  bg: string;
  border: string;
  features: Feature[];
}

const WORKFLOW_STEPS = [
  { num: "01", label: "Capture", detail: "Log meetings, decisions, and risks in structured records. Paste raw text for AI extraction, or type directly — your choice." },
  { num: "02", label: "Structure", detail: "Requirements are typed, tagged, versioned, and flagged for PHI, domain, and priority — with or without AI." },
  { num: "03", label: "Analyse", detail: "Run compliance checks, impact analysis, and story generation. Each is a BA-reviewed draft before anything saves." },
  { num: "04", label: "Validate", detail: "Generate Gherkin test cases, run them, log results. Traceability from req → test → result." },
  { num: "05", label: "Handover", detail: "Any new BA opens Catch Me Up and sees the full project story — decisions, meetings, risks, in order." },
];

const FEATURE_GROUPS: FeatureGroup[] = [
  {
    label: "Project & Planning",
    color: "text-[#1e3a6e]",
    bg: "bg-[#eef4fd]",
    border: "border-[#a8c4f0]",
    features: [
      { icon: Inbox,        name: "Catch Me Up",   desc: "Chronological feed of every meeting, decision, risk, and change — for new joiners or anyone returning after time away. AI generates an onboarding brief on demand." },
      { icon: NotebookPen,  name: "Meeting Notes",  desc: "Log meetings with structured attendees linked to stakeholder records. AI extracts requirements from transcripts." },
      { icon: ArrowRightLeft, name: "Crosswalk",   desc: "Three-column visual map: Workflow Stages → Skills & Tools → People. See who owns what at every stage." },
      { icon: PenTool,      name: "Diagram",        desc: "Draw system architecture and flow diagrams with a Mermaid-powered canvas." },
      { icon: GitBranch,    name: "User Stories",   desc: "Write Agile user stories. Click any card to edit. AI can generate drafts from requirements.", ai: true },
      { icon: Columns3,     name: "Board",          desc: "Kanban sprint board. Drag stories across To Do → In Progress → Done." },
      { icon: CalendarDays, name: "Timeline",       desc: "Visual Gantt timeline with sprint bands. Sprint Shift cascades all downstream stories with a logged reason." },
      { icon: TrendingUp,   name: "Velocity",       desc: "Sprint velocity chart. Track team throughput over time." },
      { icon: CalendarRange,name: "Schedule",       desc: "Log BA hours by team member. Weekly capacity and overload alerts." },
      { icon: Users,        name: "Team",           desc: "Add team members, set capacity, log time off. Feeds into Schedule." },
      { icon: Users,        name: "Stakeholders",   desc: "Map stakeholders across 8 domains (Healthcare, Finance, Solo/Startup, and more) with domain-aware role presets. Auto-assigns RACI. Links to meeting attendees." },
    ],
  },
  {
    label: "Requirements & Knowledge",
    color: "text-[#1a3a5c]",
    bg: "bg-[#e8f2fb]",
    border: "border-[#90b8e8]",
    features: [
      { icon: ClipboardList, name: "Requirements",  desc: "Create, version, and manage structured requirements with PHI flags, domains, priorities, and delete/edit controls.", ai: true },
      { icon: Scale,         name: "Decisions",     desc: "Log key project decisions with rationale, owner, and impact level. Feeds into Sprint Shift and audit trail." },
      { icon: ShieldAlert,   name: "Risks",         desc: "Risk register with likelihood × impact matrix, mitigation steps, and owner. Surfaces in Catch Me Up." },
      { icon: BarChart3,     name: "Analysis",      desc: "General project analysis — open questions and notes." },
      { icon: FileText,      name: "Documents",     desc: "Upload and manage policy documents, specs, and reference material." },
      { icon: BookOpen,      name: "Knowledge Base",desc: "RAG-powered KB. AI searches your documents to provide citations in every analysis.", ai: true },
      { icon: GraduationCap, name: "Case Studies",  desc: "Store real-world case study references that inform requirement decisions." },
    ],
  },
  {
    label: "Optional AI Analysis",
    color: "text-[#1c2e78]",
    bg: "bg-[#e4ecfb]",
    border: "border-[#7aa8e8]",
    features: [
      { icon: ShieldCheck,       name: "Compliance",     desc: "Two-layer legal check: deterministic rule engine + AI IRAC reasoning. Cites exact regulation text with penalty exposure.", ai: true },
      { icon: Zap,               name: "Impact Analysis",desc: "Map which systems, stakeholders, and risks are affected by a requirement change.", ai: true },
      { icon: MessageSquareText, name: "Meeting Intel",  desc: "Paste a meeting transcript. AI extracts every distinct requirement mentioned.", ai: true },
      { icon: BriefcaseBusiness, name: "Job Intel",      desc: "Analyse a job posting. AI extracts required skills, seniority signals, and culture fit indicators.", ai: true },
      { icon: Sparkles,          name: "Genius Labs",    desc: "Mindmap generator and idea connector. Turns conversations into visual knowledge maps.", ai: true },
    ],
  },
  {
    label: "Quality & Audit",
    color: "text-[#163060]",
    bg: "bg-[#dce8fb]",
    border: "border-[#5a94d8]",
    features: [
      { icon: FlaskConical, name: "Test Cases",    desc: "Generate Gherkin BDD test scenarios from requirements. AI drafts, you approve.", ai: true },
      { icon: TestTube2,    name: "Test Runner",   desc: "Log test execution results and track pass/fail status per scenario." },
      { icon: Bot,          name: "Agent Log",     desc: "Full audit trail of every AI call: input, output, model, duration. Required in regulated environments." },
      { icon: BookMarked,   name: "Evolution Log", desc: "Log every AI mistake. What went wrong, the correction, and the lesson. Builds a self-improving skill record." },
      { icon: PackageOpen,  name: "Handover",      desc: "Generate structured handover packs for dev teams at the end of each analysis phase." },
    ],
  },
];

const AI_AGENTS = [
  { icon: ScrollText,       name: "Requirements Structurer", model: "Groq / Llama 3.3", desc: "Turns raw text into typed, tagged requirement records with PHI detection, domain classification, and priority inference." },
  { icon: GitBranch,        name: "User Story Generator",    model: "Groq / Llama 3.3", desc: "Converts a structured requirement into 2–4 Agile stories with acceptance criteria, priority, and story points." },
  { icon: Scale,            name: "Legal Compliance",        model: "Rule engine + Groq", desc: "Layer 1: deterministic rules (HIPAA, state law). Layer 2: AI IRAC reasoning with exact citations and remediation steps." },
  { icon: Zap,              name: "Impact Analyst",          model: "Groq / Llama 3.3", desc: "Identifies affected systems, stakeholders, risks, and dependencies for any requirement change." },
  { icon: Microscope,       name: "Gherkin Generator",       model: "Groq / Llama 3.3", desc: "Writes BDD test scenarios in Given/When/Then format, covering happy paths, edge cases, and compliance scenarios." },
  { icon: MessageSquareText,name: "Meeting Intel",           model: "Groq / Llama 3.3", desc: "Reads meeting transcripts and extracts every distinct software requirement discussed, with raw text citations." },
];

const PRINCIPLES = [
  {
    icon: Lock,
    title: "Your data stays yours.",
    body: "Core features — meeting logs, requirements, decisions, risks, Sprint Shift, Catch Me Up — require zero external API calls. Nothing leaves your system unless you choose to run an AI feature.",
  },
  {
    icon: Brain,
    title: "AI reads. You decide.",
    body: "Every AI output is a draft for your review. No analysis is saved or acted on until you approve it. The BA retains full decision authority and legal responsibility.",
  },
  {
    icon: Cpu,
    title: "Agent runtime, not chatbot.",
    body: "Each AI feature is a structured agent with defined input, output format, and verification layer — not a free-form chat. Predictable, auditable, improvable.",
  },
  {
    icon: BookMarked,
    title: "Built for handover.",
    body: "Every decision, meeting, and change is structured so the next BA can catch up in one tab — not by reading your notes. Knowledge doesn't leave with the person.",
  },
];

export default function BAStudioGuide() {
  return (
    <div className="min-h-full bg-background">

      {/* HERO */}
      <div className="bg-[#1e3a6e] text-white px-4 sm:px-8 py-8 sm:py-14">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-[0.25em] text-white/50 mb-4 font-mono">BA Studio · Complete Guide</p>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-5" style={{ fontFamily: "Georgia, serif" }}>
            The knowledge layer<br />that survives the handover.
          </h1>
          <p className="text-base sm:text-lg text-white/70 leading-relaxed max-w-xl">
            BA Studio is a structured project workspace for Business Analysts — with optional AI when you need it, and{" "}
            <span className="text-white font-semibold">zero cloud dependency for everything else.</span>
          </p>
          <div className="flex flex-wrap gap-6 sm:gap-8 mt-10">
            {[
              { value: "0",    label: "Required API calls for core features" },
              { value: "AI?",  label: "Optional — not the point" },
              { value: "100%", label: "BA-controlled" },
            ].map(s => (
              <div key={s.label}>
                <div className="text-3xl font-bold">{s.value}</div>
                <div className="text-xs text-white/50 uppercase tracking-wider mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12 space-y-12 sm:space-y-16">

        {/* WORKFLOW */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-primary font-semibold mb-8">How it flows</h2>
          <div className="relative">
            <div className="hidden md:block absolute top-7 left-0 right-0 h-px bg-[#a8c4f0] mx-16" />
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {WORKFLOW_STEPS.map((step, i) => (
                <div key={step.num} className="relative flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-full bg-[#1e3a6e] text-white flex items-center justify-center text-lg font-bold shrink-0 mb-3 relative z-10">
                    {step.num}
                  </div>
                  <p className="text-sm font-bold text-foreground mb-1">{step.label}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{step.detail}</p>
                  {i < WORKFLOW_STEPS.length - 1 && (
                    <ChevronRight className="md:hidden h-4 w-4 text-[#a8c4f0] mt-2" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FEATURE GROUPS */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-primary font-semibold mb-8">Every feature, explained</h2>
          <div className="space-y-8">
            {FEATURE_GROUPS.map((group) => (
              <div key={group.label}>
                <div className={`inline-flex items-center gap-2 ${group.bg} ${group.border} border rounded-full px-3 py-1 mb-4`}>
                  <span className={`text-[11px] font-bold uppercase tracking-wider ${group.color}`}>{group.label}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {group.features.map((f) => (
                    <div key={f.name} className={`${group.bg} border ${group.border} rounded-lg p-4 flex gap-3`}>
                      <div className="shrink-0 mt-0.5">
                        <f.icon className={`h-4 w-4 ${group.color}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className={`text-xs font-bold ${group.color}`}>{f.name}</span>
                          {f.ai && (
                            <span className="text-[9px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-semibold uppercase tracking-wide">AI opt-in</span>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* AI AGENTS */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">Optional AI agents</h2>
          <p className="text-sm text-muted-foreground mb-8">
            Each agent is a structured workflow — defined input, verified output format, BA approval before anything is saved.
            Run them when you want them. Skip them when your data stays internal.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {AI_AGENTS.map((agent) => (
              <div key={agent.name} className="bg-card border border-border rounded-lg p-5 flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#1e3a6e] flex items-center justify-center shrink-0">
                  <agent.icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground mb-0.5">{agent.name}</p>
                  <p className="text-[10px] text-primary font-mono mb-2">{agent.model}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{agent.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* PRINCIPLES */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-primary font-semibold mb-8">Design principles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PRINCIPLES.map((p) => (
              <div key={p.title} className="border-l-4 border-primary pl-5">
                <p.icon className="h-5 w-5 text-primary mb-3" />
                <p className="text-sm font-bold text-foreground mb-2">{p.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* QUICK START */}
        <section>
          <div className="bg-[#e4ecfb] border border-[#a8c4f0] rounded-xl p-8">
            <h2 className="text-xs uppercase tracking-widest text-primary font-semibold mb-6">Quick start — 3 steps</h2>
            <div className="space-y-4">
              {[
                {
                  n: "1",
                  title: "Create a project",
                  body: 'Go to Start Here → "New Project". Give it a name and choose Agile or Waterfall.',
                  check: "Project, team, and timeline are ready.",
                },
                {
                  n: "2",
                  title: "Add your stakeholders and requirements",
                  body: "Open Stakeholders → pick your domain (Healthcare, Finance, Solo, etc.) → roles pre-fill. Then go to Requirements → type directly or paste raw text and let AI structure it.",
                  check: "Stakeholder RACI and structured requirements ready — no AI required for either.",
                },
                {
                  n: "3",
                  title: "Log a meeting and check Catch Me Up",
                  body: "Go to Meeting Notes → add attendees from your stakeholder list → log the notes. Then open Catch Me Up to see the full project timeline in one view.",
                  check: "Any new team member can onboard from Catch Me Up without asking anyone.",
                },
              ].map((step) => (
                <div key={step.n} className="flex gap-4 bg-white rounded-lg p-4 border border-[#a8c4f0]">
                  <div className="w-8 h-8 rounded-full bg-[#1e3a6e] text-white text-sm font-bold flex items-center justify-center shrink-0">
                    {step.n}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground mb-1">{step.title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-2">{step.body}</p>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span className="text-[11px] text-primary font-medium">{step.check}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EVOLUTION NOTE */}
        <section>
          <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-lg px-5 py-4">
            <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">
              <span className="font-semibold">When AI gets it wrong —</span> use the Evolution Log tab to record the mistake, the correct output, and the lesson. Over time this builds a project-specific improvement record that makes the agents smarter for your domain.
            </p>
          </div>
        </section>

        {/* BUILT FROM EXPERIENCE */}
        <section className="pb-8">
          <div className="bg-[#1e3a6e] rounded-xl overflow-hidden">
            <div className="px-4 sm:px-8 pt-8 sm:pt-10 pb-6 border-b border-white/10">
              <p className="text-xs uppercase tracking-widest text-white/40 font-mono mb-3">Why this exists</p>
              <h2 className="text-xl sm:text-2xl font-bold text-white leading-snug" style={{ fontFamily: "Georgia, serif" }}>
                Trained at scale. Built from the inside.
              </h2>
            </div>

            <div className="px-4 sm:px-8 py-6 sm:py-8 space-y-6 text-white/65 text-sm font-mono leading-relaxed">
              <p>
                This tool was not designed by someone who read about BA problems. It was built by someone who spent years inside one of the largest property and casualty insurance operations in the United States — navigating a landscape that most enterprise architects only see on whiteboards.
              </p>

              <div className="border-l-2 border-[#7eaaff]/30 pl-6 space-y-4">
                <p>
                  The core system was a mainframe. Decades old. Nobody fully understood it. Nobody could replace it. Every modern system built afterward — CRM, policy center, claims platform, brokerage portal — was layered on top of it, not integrated with it.
                </p>
                <p>
                  Over fifteen years, the organization migrated through three different CRM platforms. Each migration inherited the undocumented logic of the last. By the third, the institutional knowledge of <span className="text-white italic">why</span> certain fields existed had long migrated out with the people who left.
                </p>
                <p>
                  Six systems. No integration. Agents re-entering the same data by hand across claims, life, personal lines, commercial lines, brokerage, and CRM — every single day.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Quote className="h-4 w-4 text-[#7eaaff] shrink-0 mt-0.5" />
                <p className="text-white/80 italic">
                  "The people were the integration layer. And every migration made it worse — because the requirements that should have been documented weren't."
                </p>
              </div>

              <p>
                That environment teaches you to think at scale. Not about features — about systems, dependencies, compliance exposure, and what survives when the people who built it are gone.
              </p>

              <p>
                BA Studio is the tool that should have existed in those rooms. Not because of the AI — but because of the structure. Because the next person walking in deserves to understand what happened, who decided it, and why. Without asking anyone.
              </p>

              <p className="text-white/40 text-xs">
                Industry: Property &amp; Casualty Insurance · Scale: Top-10 U.S. carrier · Systems: Mainframe, claims platform, three CRM generations, multiple disconnected policy centers
              </p>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
